import { Heading } from '@backend/lib-design-system';
import { useRouter } from 'next/router';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import type { SearchJobOfferOutput } from '~/business/JobOffer/searchJobOffers';
import { Button } from '~/components/Button';
import { MissionListCard } from '~/components/MissionsList/MissionListCard';
import { MissionListPaginationBar } from '~/components/MissionsList/MissionListPaginationBar';
import { useTranslation } from '~/hooks';
import { useHref } from '~/hooks/useHref';
import { getHubPageHref, type HubPagination } from '~/utils/hubPage';

import {
  ButtonContainer,
  CardsContainer,
  CardWrapper,
  ContentWrapper,
  NavigationBackStyledIcon,
  NavigationButton,
  NavigationContainer,
  NavigationNextStyledIcon,
  PaginationContainer,
  TitleWrapper,
  Wrapper,
} from './LatestJobOffersCard.styles';

const CARD_WIDTH = 355 + 40;

interface LatestJobOfferCardProps {
  latestJobOffers: SearchJobOfferOutput[];
  onMissionClick?: (jobOffer: SearchJobOfferOutput) => void;
  showDistance?: boolean;
  title?: string;
  hideButton?: boolean;
  seeAllOffersPath?: string;
  /** the pages of the offers, linked under the cards */
  pagination?: HubPagination;
}

export const LatestJobOffersCard: FC<LatestJobOfferCardProps> = ({
  latestJobOffers,
  onMissionClick = () => {},
  showDistance = false,
  title,
  hideButton = false,
  seeAllOffersPath,
  pagination,
}) => {
  const t = useTranslation();
  const { push, asPath } = useRouter();
  const { baseHref } = useHref();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1); // -1 for rounding issues
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // another page of offers starts from its first card, wherever the previous one was scrolled
    container.scrollLeft = 0;
    checkScrollPosition();

    container.addEventListener('scroll', checkScrollPosition);

    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
      resizeObserver.disconnect();
    };
  }, [latestJobOffers, checkScrollPosition]);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -CARD_WIDTH,
        behavior: 'smooth',
      });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: CARD_WIDTH,
        behavior: 'smooth',
      });
    }
  }, []);

  // The early return must stay below every hook: hooks may not be skipped between renders
  // (the list can switch between empty and non-empty on client-side navigation)
  if (!latestJobOffers?.length) {
    return null;
  }

  const handleSeeAllOffers = async () => {
    await push(seeAllOffersPath || baseHref);
  };

  const pagePath = asPath.split(/[?#]/)[0];

  const handlePageChange = async (page: number) => {
    await push(getHubPageHref(pagePath, page));
  };

  return (
    <Wrapper>
      <ContentWrapper>
        <TitleWrapper>
          <Heading variant="heading-2">{title || t('latest_job_offers.title')}</Heading>
        </TitleWrapper>
        <NavigationContainer>
          {canScrollLeft && (
            <NavigationButton type="button" onClick={scrollLeft} aria-label="Scroll left">
              <NavigationBackStyledIcon size="m" />
            </NavigationButton>
          )}
          {canScrollRight && (
            <NavigationButton type="button" onClick={scrollRight} aria-label="Scroll right">
              <NavigationNextStyledIcon size="m" />
            </NavigationButton>
          )}
        </NavigationContainer>
        <CardsContainer ref={scrollContainerRef}>
          {latestJobOffers.map(jobOffer => (
            <CardWrapper key={jobOffer.refId} $showAll={Boolean(pagination)}>
              <MissionListCard jobOffer={jobOffer} onMissionClick={onMissionClick} showDistance={showDistance} />
            </CardWrapper>
          ))}
        </CardsContainer>
        {pagination && pagination.pageCount > 1 && (
          <PaginationContainer>
            <MissionListPaginationBar
              pageCount={pagination.pageCount}
              currentPage={pagination.currentPage}
              getPageHref={page => getHubPageHref(pagePath, page)}
              onChangePage={handlePageChange}
            />
          </PaginationContainer>
        )}
        {!hideButton && (
          <ButtonContainer>
            <Button size="xl" variant="rounded" onClick={handleSeeAllOffers}>
              {t('missions.see_all_offers')}
            </Button>
          </ButtonContainer>
        )}
      </ContentWrapper>
    </Wrapper>
  );
};
